/**
 * Serviço de Configuração - Gerencia APIs e configurações do sistema
 */

export interface APIConfig {
  apiKey: string;
  baseURL: string;
  isConfigured: boolean;
  provider: string;
}

export interface SystemConfig {
  openai: APIConfig;
  anthropic: APIConfig;
  google: APIConfig;
  stripe: APIConfig;
}

class ConfigService {
  private config: SystemConfig;
  private initialized = false;

  constructor() {
    this.config = this.initializeConfig();
    this.validateConfiguration();
  }

  private initializeConfig(): SystemConfig {
    return {
      openai: {
        apiKey: import.meta.env.VITE_OPENAI_API_KEY || '',
        baseURL: 'https://api.openai.com/v1',
        isConfigured: false,
        provider: 'OpenAI'
      },
      anthropic: {
        apiKey: import.meta.env.VITE_ANTHROPIC_API_KEY || '',
        baseURL: import.meta.env.DEV ? '/api/anthropic' : 'https://api.anthropic.com',
        isConfigured: false,
        provider: 'Anthropic'
      },
      google: {
        apiKey: import.meta.env.VITE_GOOGLE_API_KEY || '',
        baseURL: 'https://generativelanguage.googleapis.com',
        isConfigured: false,
        provider: 'Google'
      },
      stripe: {
        apiKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
        baseURL: 'https://api.stripe.com',
        isConfigured: false,
        provider: 'Stripe'
      }
    };
  }

  private validateConfiguration(): void {
    console.log('🔧 Validando configuração das APIs...');

    Object.entries(this.config).forEach(([key, config]) => {
      const hasValidKey = this.isValidAPIKey(config.apiKey, key as keyof SystemConfig);
      config.isConfigured = hasValidKey;
      
      const status = hasValidKey ? '✅ Configurada' : '❌ Não configurada';
      console.log(`${config.provider}: ${status}`);
      
      if (!hasValidKey) {
        console.warn(`⚠️ ${config.provider} não configurada. Configure VITE_${key.toUpperCase()}_API_KEY no arquivo .env`);
      }
    });

    this.initialized = true;
  }

  private isValidAPIKey(apiKey: string, provider: keyof SystemConfig): boolean {
    if (!apiKey || apiKey.length < 10) return false;
    
    // Verificar se não é uma chave de placeholder
    const placeholders = [
      'your-api-key',
      'sua-chave',
      'demo-key',
      'example',
      'placeholder'
    ];
    
    const isPlaceholder = placeholders.some(placeholder => 
      apiKey.toLowerCase().includes(placeholder)
    );
    
    if (isPlaceholder) return false;

    // Verificar formato específico por provider
    switch (provider) {
      case 'openai':
        return apiKey.startsWith('sk-') && apiKey.length > 20;
      case 'anthropic':
        return apiKey.startsWith('sk-ant-') && apiKey.length > 20;
      case 'google':
        return apiKey.length > 20 && !apiKey.startsWith('sk-');
      case 'stripe':
        return apiKey.startsWith('pk_') && apiKey.length > 20;
      default:
        return true;
    }
  }

  /**
   * Obter configuração de um provider específico
   */
  getProviderConfig(provider: keyof SystemConfig): APIConfig {
    if (!this.initialized) {
      throw new Error('ConfigService não foi inicializado');
    }
    
    const config = this.config[provider];
    if (!config) {
      throw new Error(`Provider '${provider}' não encontrado`);
    }
    
    return config;
  }

  /**
   * Verificar se um provider está configurado
   */
  isProviderConfigured(provider: keyof SystemConfig): boolean {
    return this.getProviderConfig(provider).isConfigured;
  }

  /**
   * Obter lista de providers configurados
   */
  getConfiguredProviders(): string[] {
    return Object.entries(this.config)
      .filter(([_, config]) => config.isConfigured)
      .map(([provider]) => provider);
  }

  /**
   * Obter status geral da configuração
   */
  getConfigurationStatus(): {
    totalProviders: number;
    configuredProviders: number;
    hasAnyConfigured: boolean;
    missingProviders: string[];
  } {
    const total = Object.keys(this.config).length;
    const configured = this.getConfiguredProviders();
    const missing = Object.entries(this.config)
      .filter(([_, config]) => !config.isConfigured)
      .map(([provider, config]) => config.provider);

    return {
      totalProviders: total,
      configuredProviders: configured.length,
      hasAnyConfigured: configured.length > 0,
      missingProviders: missing
    };
  }

  /**
   * Validar se pelo menos um provider está configurado
   */
  validateMinimumConfiguration(): void {
    const status = this.getConfigurationStatus();
    
    if (!status.hasAnyConfigured) {
      throw new Error(
        'Nenhuma API de IA configurada. Configure pelo menos uma chave de API no arquivo .env:\n' +
        '- VITE_OPENAI_API_KEY=sk-sua-chave-openai\n' +
        '- VITE_ANTHROPIC_API_KEY=sk-ant-sua-chave-anthropic\n' +
        '- VITE_GOOGLE_API_KEY=sua-chave-google\n' +
        '- VITE_STRIPE_PUBLISHABLE_KEY=pk_sua-chave-stripe'
      );
    }
  }

  /**
   * Recarregar configuração (útil para desenvolvimento)
   */
  reload(): void {
    this.config = this.initializeConfig();
    this.validateConfiguration();
  }
}

// Instância singleton
export const configService = new ConfigService();

// Exportar tipos para uso em outros arquivos
export type { SystemConfig, APIConfig };
